import React, { useState, useEffect } from "react";
import { EnvironmentConfig } from "../config/environmentConfig";

const DATA_URL = "https://global.occtoo.com/alligotest/etim/v6/";
const managementBaseUrl = EnvironmentConfig.api.baseUrl;

function ValuePage() {
  const [valueList, setValueList] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editValue, setEditValue] = useState({});
  const [newValue, setNewValue] = useState({
    valueCode: "",
    displayName: "",
    description: "",
    tooltip: "",
    image: "",
    oldvalueId: "",
    alternativeKey: "",
  });
  const [searchTerm, setSearchTerm] = useState(""); // ✅ Nytt för sökning
  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ Filtrering och pagination
  const filteredValues = valueList.filter(
    (v) =>
      v.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.valueCode.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredValues.length / itemsPerPage);
  const paginatedValues = filteredValues.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ✅ Hämta data från Occtoo
  useEffect(() => {
    const fetchData = async () => {
      try {
        const dataRes = await fetch(`${managementBaseUrl}/valuesupdated?top=300&language=sv`);
        const data = await dataRes.json();
        setValueList(data.results);
      } catch (error) {
        console.error("Fel vid hämtning från Occtoo:", error);
      }
    };
    fetchData();
  }, []);

  // ✅ Lägg till nytt värde
  const handleAdd = async () => {
    const updated = [...valueList, newValue];
    setValueList(updated);
    try {
      const res = await fetch("http://localhost:5000/values", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newValue),
      });
      const data = await res.json();
      console.log("Sparat i Occtoo via backend:", data);
    } catch (error) {
      console.error("Fel vid sparning:", error);
    }
    setNewValue({
      valueCode: "",
      displayName: "",
      description: "",
      tooltip: "",
      image: "",
      oldvalueId: "",
      alternativeKey: "",
    });
  };

  // ✅ Radera värde
  const handleDelete = async (id) => {
    const updated = valueList.filter((v) => v.valueCode !== id);
    setValueList(updated);
    try {
      const response = await fetch(`http://localhost:5000/values/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Misslyckades att radera värde");
      }
      const data = await response.json();
      console.log("Value raderad:", data);
      alert("Feature raderad i Occtoo!");
    } catch (error) {
      console.error("Fel vid DELETE:", error);
    }
  };

  // ✅ Redigera värde
  const handleEdit = (value) => {
    setEditId(value.valueCode);
    setEditValue({ ...value });
  };

  const handleSaveEdit = async () => {
    const updated = valueList.map((v) => (v.valueCode === editId ? editValue : v));
    setValueList(updated);
    setEditId(null);
    setEditValue({});
    try {
      const response = await fetch(`http://localhost:5000/values/${editId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editValue),
      });
      if (!response.ok) {
        throw new Error("Misslyckades att uppdatera value");
      }
      const data = await response.json();
      console.log("Value uppdaterad:", data);
    } catch (error) {
      console.error("Fel vid PUT:", error);
    }
  };

  const fieldLabels = {
    valueCode: "ID",
    displayName: "Visningsnamn",
    description: "Beskrivning",
    tooltip: "Tooltip",
    image: "Symbol",
    alternativeKey: "Alternativ nyckel",
  };

  return (
    <div className="container mt-4">
      <h3 className="mb-4">Hantera Värden</h3>

      {/* Formulär för nytt värde */}
      <div className="card p-3 mb-4">
        <h5 className="mb-3">Lägg till nytt värde</h5>
        <div className="row g-2">
          {["valueCode", "displayName", "description", "tooltip", "image", "alternativeKey"].map(
            (field, idx) => (
              <div className="col-md-2" key={idx}>
                <input
                  className="form-control form-control-sm"
                  placeholder={fieldLabels[field]}
                  value={newValue[field] || ""}
                  onChange={(e) => setNewValue({ ...newValue, [field]: e.target.value })}
                />
              </div>
            )
          )}
        </div>
        <div className="d-grid gap-2 d-md-flex justify-content-md-end">
          <button className="btn btn-outline-dark btn-sm mt-3" onClick={handleAdd}>
            Lägg till
          </button>
        </div>
      </div>

      {/* ✅ Sökfält */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Sök värde..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1); // Återställ till första sidan vid sökning
          }}
        />
      </div>

      {/* Tabell med värden */}
      <table className="table table-bordered table-striped">
        <thead className="table-light">
          <tr>
            <th>ID</th>
            <th>Namn</th>
            <th>Beskrivning</th>
            <th>Tooltip</th>
            <th>Symbol</th>
            <th>Alternativ nyckel</th>
            <th>Åtgärder</th>
          </tr>
        </thead>
        <tbody>
          {paginatedValues.map((v) => (
            <tr key={v.valueCode}>
              {editId === v.valueCode ? (
                <>
                  {[
                    "valueCode",
                    "displayName",
                    "description",
                    "tooltip",
                    "image",
                    "alternativeKey",
                  ].map((field) => (
                    <td key={field}>
                      <input
                        className="form-control form-control-sm"
                        value={editValue[field] || ""}
                        onChange={(e) => setEditValue({ ...editValue, [field]: e.target.value })}
                      />
                    </td>
                  ))}
                  <td>
                    <button className="btn btn-success btn-sm me-2" onClick={handleSaveEdit}>
                      Spara
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditId(null)}>
                      Avbryt
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td>{v.valueCode}</td>
                  <td>{v.displayName}</td>
                  <td>{v.description}</td>
                  <td>{v.tooltip}</td>
                  <td>{v.image ? <img src={v.image} alt="" style={{ width: "30px" }} /> : "-"}</td>
                  <td>{v.alternativeKey || "-"}</td>
                  <td>
                    <button className="btn btn-dark btn-sm me-2" onClick={() => handleEdit(v)}>
                      Redigera
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(v.valueCode)}
                    >
                      Radera
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="mt-3">
          <ul className="pagination">
            {[...Array(totalPages)].map((_, index) => (
              <li key={index} className={`page-item ${currentPage === index + 1 ? "active" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(index + 1)}>
                  {index + 1}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}

export default ValuePage;
